import React, { useState, useRef } from "react";
import { toast } from "sonner";
import { User, Camera, X } from "lucide-react";
import { LicenseCropTool, ImageCropSelector, DriverLicenseLightbox } from "./ImageCropTools";

// Photo Upload Component with Driver License support and two-step crop flow
export const PhotoUpload = ({ photo, cropPosition, onPhotoChange, existingPhoto, existingCropPosition }) => {
  const fileInputRef = useRef(null);
  const [showLightbox, setShowLightbox] = useState(false);
  const [rawImage, setRawImage] = useState(null);
  const [croppedLicense, setCroppedLicense] = useState(null);
  const [showLicenseCrop, setShowLicenseCrop] = useState(false);
  const [showFaceSelector, setShowFaceSelector] = useState(false);
  const [isEditingCrop, setIsEditingCrop] = useState(false);
  
  const displayPhoto = photo || existingPhoto;
  const displayCrop = cropPosition || existingCropPosition || { x: 25, y: 30, zoom: 2 };
  
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be less than 10MB");
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setRawImage(reader.result);
      setIsEditingCrop(false);
      setShowLicenseCrop(true);
    };
    reader.readAsDataURL(file);
    
    e.target.value = '';
  };
  
  const handleLicenseCropConfirm = (croppedImage) => {
    setCroppedLicense(croppedImage);
    setShowLicenseCrop(false);
    setShowFaceSelector(true);
  };
  
  const handleLicenseCropCancel = () => {
    setRawImage(null);
    setShowLicenseCrop(false);
  };
  
  const handleEditFaceSelection = () => {
    setCroppedLicense(displayPhoto);
    setIsEditingCrop(true);
    setShowFaceSelector(true);
  };
  
  const handleFaceSelectorConfirm = (cropData) => {
    onPhotoChange(croppedLicense, cropData);
    setCroppedLicense(null);
    setRawImage(null);
    setShowFaceSelector(false);
    setIsEditingCrop(false);
  };
  
  const handleFaceSelectorCancel = () => {
    if (isEditingCrop) {
      setCroppedLicense(null);
      setShowFaceSelector(false);
      setIsEditingCrop(false);
    } else {
      setShowFaceSelector(false);
      setShowLicenseCrop(true);
    }
  };
  
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative">
        {displayPhoto ? (
          <div className="relative">
            <div 
              className="w-24 h-24 rounded-full border-4 border-white shadow-lg cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all"
              onClick={() => setShowLightbox(true)}
              title="Click to view full driver license"
              style={{
                backgroundImage: `url(${displayPhoto})`,
                backgroundSize: `${(displayCrop.zoom || 2) * 100}%`,
                backgroundPosition: `${displayCrop.x}% ${displayCrop.y}%`,
                backgroundRepeat: 'no-repeat'
              }}
            />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onPhotoChange(null, null);
              }}
              className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors z-10"
              title="Remove photo"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="w-24 h-24 rounded-full bg-slate-100 border-4 border-white shadow-lg flex items-center justify-center">
            <User className="w-10 h-10 text-slate-400" />
          </div>
        )}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="absolute -bottom-1 -right-1 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors shadow-md"
          title="Upload new photo"
        >
          <Camera className="w-4 h-4" />
        </button>
      </div>
      
      {displayPhoto && (
        <button
          type="button"
          onClick={handleEditFaceSelection}
          className="text-xs text-blue-600 hover:text-blue-700 underline"
        >
          Edit face selection
        </button>
      )}
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
      <p className="text-xs text-slate-500 text-center">
        {displayPhoto ? "Click photo to view license" : "Upload driver license photo"}
      </p>
      
      {showLicenseCrop && rawImage && (
        <LicenseCropTool 
          image={rawImage}
          onConfirm={handleLicenseCropConfirm}
          onCancel={handleLicenseCropCancel}
        />
      )}
      
      {showFaceSelector && croppedLicense && (
        <ImageCropSelector 
          image={croppedLicense}
          initialPosition={isEditingCrop ? displayCrop : null}
          onConfirm={handleFaceSelectorConfirm}
          onCancel={handleFaceSelectorCancel}
        />
      )}
      
      <DriverLicenseLightbox 
        photo={displayPhoto}
        isOpen={showLightbox}
        onClose={() => setShowLightbox(false)}
      />
    </div>
  );
};
